import { computed, type ComputedRef } from 'vue'
import { useInstitutionStore } from './useInstitutionStore'

export interface BranchAvailability {
  /** Принимает ли точка заказы (false = «СТОП Приём заказов» в админ-панели) */
  acceptingOrders: ComputedRef<boolean>
  /** Активен ли «СТОП Доставка» (доставка остановлена, самовывоз/в зале работают) */
  deliveryStopped: ComputedRef<boolean>
  /** Можно ли оформить доставку прямо сейчас */
  canDelivery: ComputedRef<boolean>
  /** Можно ли оформить самовывоз / заказ в зале */
  canTakeawayOrDineIn: ComputedRef<boolean>
  /** Полностью ли заблокирован любой заказ */
  isFullyBlocked: ComputedRef<boolean>
  /** Человекочитаемая причина блокировки (или null, если всё ок) */
  blockReason: ComputedRef<string | null>
  /** То же, но для конкретного способа получения */
  blockReasonFor: (
    methodReceipt: 'delivery' | 'takeaway' | 'dine_in' | string | null | undefined
  ) => string | null
}

/**
 * Единая точка истины по доступности выбранного филиала на гостевом фронте.
 *
 * Источник данных — `useInstitutionStore.selectedBranch`. Поля
 * `accepting_orders` и `delivery_stopped` приходят с бэка в DTO branch и
 * обновляются поллингом из useInstitutionAutoRefresh каждую минуту.
 *
 * Используется в:
 * - WorkloadBlockedBanner (глобальный баннер сверху)
 * - useBasket isOrderDisabled / tooltipText / orderButtonLabel
 * - ModalMapYandex (дизейбл таба «Доставка»)
 */
export function useBranchAvailability(): BranchAvailability {
  const storeInstitution = useInstitutionStore()

  // selectedBranch на фронте имеет accepting_orders и delivery_stopped опционально;
  // если бэк ещё не вернул эти поля (старая версия / cold cache) — считаем что всё работает.
  const acceptingOrders = computed<boolean>(() => {
    const v = (storeInstitution.selectedBranch as any)?.accepting_orders
    return v === undefined ? true : !!v
  })

  const deliveryStopped = computed<boolean>(() => {
    const v = (storeInstitution.selectedBranch as any)?.delivery_stopped
    return v === undefined ? false : !!v
  })

  const canDelivery = computed<boolean>(() => acceptingOrders.value && !deliveryStopped.value)
  const canTakeawayOrDineIn = computed<boolean>(() => acceptingOrders.value)
  const isFullyBlocked = computed<boolean>(() => !acceptingOrders.value)

  const blockReason = computed<string | null>(() => {
    if (!acceptingOrders.value) return 'Заведение временно не принимает заказы'
    if (deliveryStopped.value) return 'Доставка временно остановлена'
    return null
  })

  function blockReasonFor(
    methodReceipt: 'delivery' | 'takeaway' | 'dine_in' | string | null | undefined,
  ): string | null {
    if (!acceptingOrders.value) return 'Заведение временно не принимает заказы'
    if (methodReceipt === 'delivery' && deliveryStopped.value) {
      return 'Доставка временно остановлена. Выберите самовывоз или заказ в зале'
    }
    return null
  }

  return {
    acceptingOrders,
    deliveryStopped,
    canDelivery,
    canTakeawayOrDineIn,
    isFullyBlocked,
    blockReason,
    blockReasonFor,
  }
}
